import { c as _c } from "react/compiler-runtime";
import React, { useContext, useEffect, useRef, useState } from "react";
import { BarChart3, BookOpen, LifeBuoy, LogOut, Monitor, Moon, Palette, SettingsIcon, Sun, Wrench } from "lucide-react";
import { useTheme } from "next-themes";
import posthog from "posthog-js";
import { AuthContext } from "../AuthProvider";
import { githubLogin } from "@/lib/github-login";
import { NotificationFloatingButton } from "../NotificationFloatingButton";
import { StatusWidgetMorph, RailStatusHoverCard } from "./StatusIndicator";
import { BSErrorBoundary } from "../ui/bs-error-boundary";
import { DropdownMenu, DropdownMenuContent, DropdownMenuControlOption, DropdownMenuControlRow, DropdownMenuItem, DropdownMenuRadioGroup, DropdownMenuSeparator, DropdownMenuTrigger } from "@repo/blacksmith-ui/dropdown-menu";
import { RoutePrefetchLink } from "@/components/ui/route-prefetch-link";
import { useSetPersistedTheme } from "@/hooks/useAppearanceSettings";
import { useRoutePrefetchIntent } from "@/hooks/useRoutePrefetchIntent";
import { useIsHydrated } from "@/components/codesmith/useIsHydrated";
import { cn } from "@repo/blacksmith-ui/cn";
import { CollapsedIconTooltip, RAIL_ROW, RAIL_ROW_ACTIVE, RailRowContents } from "@repo/blacksmith-ui/app-rail";
import { RAIL_WIDTH } from "./railConstants";
export type SidebarActiveSection = "home" | "actions" | "codesmith" | "proof" | "sandboxes" | "usage" | "admin";
const THEME_OPTIONS = [{
  value: "system",
  label: "System",
  icon: Monitor
}, {
  value: "light",
  label: "Light",
  icon: Sun
}, {
  value: "dark",
  label: "Dark",
  icon: Moon
}] as const;
export function AppearanceThemeToggle() {
  const $ = _c(4);
  const {
    theme
  } = useTheme();
  const setTheme = useSetPersistedTheme();
  const mounted = useIsHydrated();
  const activeTheme = mounted ? theme ?? "system" : "system";
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = THEME_OPTIONS.map(_temp);
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] !== activeTheme || $[2] !== setTheme) {
    t1 = <DropdownMenuRadioGroup value={activeTheme} onValueChange={setTheme} className="-mr-1 flex items-center gap-0.5">{t0}</DropdownMenuRadioGroup>;
    $[1] = activeTheme;
    $[2] = setTheme;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  return t1;
}
function _temp(t0) {
  const {
    value,
    label,
    icon: Icon
  } = t0;
  return <DropdownMenuControlOption key={value} value={value} aria-label={label} textValue={label}><Icon /></DropdownMenuControlOption>;
}
function useSidebarLogout() {
  const $ = _c(2);
  const {
    logout
  } = useContext(AuthContext);
  let t0;
  if ($[0] !== logout) {
    t0 = () => {
      posthog?.reset();
      logout();
    };
    $[0] = logout;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
}
export function SidebarAdminRow(t0) {
  const $ = _c(15);
  const {
    expanded,
    active,
    onClick,
    hideLabelWhenCollapsed: t1
  } = t0;
  const hideLabelWhenCollapsed = t1 === undefined ? true : t1;
  const {
    user
  } = useContext(AuthContext);
  if (!user?.is_superuser) {
    return null;
  }
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <div className="my-2"><hr className="border-[#0c0c0d14] dark:border-white/12" /></div>;
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  const t3 = hideLabelWhenCollapsed && !expanded;
  const t4 = active ? "page" : undefined;
  const t5 = active && RAIL_ROW_ACTIVE;
  let t6;
  if ($[1] !== t5) {
    t6 = cn(RAIL_ROW, t5, "group-data-[collapsible=icon]:mx-auto group-data-[collapsible=icon]:!w-8 group-data-[collapsible=icon]:!min-w-8 group-data-[collapsible=icon]:!max-w-8 group-data-[collapsible=icon]:justify-center");
    $[1] = t5;
    $[2] = t6;
  } else {
    t6 = $[2];
  }
  let t7;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <Wrench className="size-[18px]" />;
    $[3] = t7;
  } else {
    t7 = $[3];
  }
  let t8;
  if ($[4] !== expanded || $[5] !== hideLabelWhenCollapsed) {
    t8 = <RailRowContents icon={t7} label="Admin" expanded={expanded} hideLabelWhenCollapsed={hideLabelWhenCollapsed} />;
    $[4] = expanded;
    $[5] = hideLabelWhenCollapsed;
    $[6] = t8;
  } else {
    t8 = $[6];
  }
  let t9;
  if ($[7] !== onClick || $[8] !== t4 || $[9] !== t6 || $[10] !== t8) {
    t9 = <RoutePrefetchLink href="/admin" onClick={onClick} aria-label="Admin" aria-current={t4} className={t6}>{t8}</RoutePrefetchLink>;
    $[7] = onClick;
    $[8] = t4;
    $[9] = t6;
    $[10] = t8;
    $[11] = t9;
  } else {
    t9 = $[11];
  }
  let t10;
  if ($[12] !== t3 || $[13] !== t9) {
    t10 = <>{t2}<CollapsedIconTooltip label="Admin" enabled={t3}>{t9}</CollapsedIconTooltip></>;
    $[12] = t3;
    $[13] = t9;
    $[14] = t10;
  } else {
    t10 = $[14];
  }
  return t10;
}
export function SidebarGlobalFooter(t0) {
  const $ = _c(78);
  const {
    expanded,
    currentOrg,
    settingsHref,
    activeSection,
    onSectionClick,
    className,
    hideLabelWhenCollapsed: t1,
    statusAsHoverCard: t2
  } = t0;
  const hideLabelWhenCollapsed = t1 === undefined ? true : t1;
  const statusAsHoverCard = t2 === undefined ? false : t2;
  const {
    user
  } = useContext(AuthContext);
  let t3;
  if ($[0] !== user) {
    t3 = githubLogin(user);
    $[0] = user;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const login = t3;
  const handleLogout = useSidebarLogout();
  const usageHref = currentOrg ? `/${currentOrg}/usage` : "/usage";
  const supportHref = currentOrg ? `/${currentOrg}/support` : "/support";
  const [settingsMenuOpen, setSettingsMenuOpen] = useState(false);
  const settingsTriggerRef = useRef(null);
  const settingsPrefetchIntent = useRoutePrefetchIntent(currentOrg ? settingsHref : null);
  const settingsMenuCloseTimer = useRef(null);
  let t4;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => {
      if (settingsMenuCloseTimer.current) {
        clearTimeout(settingsMenuCloseTimer.current);
        settingsMenuCloseTimer.current = null;
      }
    };
    $[2] = t4;
  } else {
    t4 = $[2];
  }
  const cancelSettingsMenuClose = t4;
  let t5;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = event => {
      if (event.pointerType === "touch") {
        return;
      }
      cancelSettingsMenuClose();
      setSettingsMenuOpen(true);
    };
    $[3] = t5;
  } else {
    t5 = $[3];
  }
  const openSettingsMenu = t5;
  let t6;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = event_0 => {
      if (event_0.pointerType === "touch") {
        return;
      }
      cancelSettingsMenuClose();
      settingsMenuCloseTimer.current = setTimeout(() => setSettingsMenuOpen(false), 120);
    };
    $[4] = t6;
  } else {
    t6 = $[4];
  }
  const scheduleSettingsMenuClose = t6;
  let t7;
  let t8;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = () => cancelSettingsMenuClose;
    t8 = [];
    $[5] = t7;
    $[6] = t8;
  } else {
    t7 = $[5];
    t8 = $[6];
  }
  useEffect(t7, t8);
  let t9;
  if ($[7] !== expanded || $[8] !== hideLabelWhenCollapsed) {
    t9 = <NotificationFloatingButton variant="sidebar" label="Notifications" expanded={expanded} hideLabelWhenCollapsed={hideLabelWhenCollapsed} />;
    $[7] = expanded;
    $[8] = hideLabelWhenCollapsed;
    $[9] = t9;
  } else {
    t9 = $[9];
  }
  const notifications = t9;
  let t10;
  if ($[10] !== expanded || $[11] !== statusAsHoverCard) {
    t10 = <BSErrorBoundary title="Status Indicator" fallback={null}><div className="pt-1">{statusAsHoverCard ? <RailStatusHoverCard railWidth={RAIL_WIDTH} /> : <StatusWidgetMorph expanded={expanded} />}</div></BSErrorBoundary>;
    $[10] = expanded;
    $[11] = statusAsHoverCard;
    $[12] = t10;
  } else {
    t10 = $[12];
  }
  const status = t10;
  let t11;
  if ($[13] !== settingsMenuOpen) {
    t11 = event_1 => {
      if (settingsMenuOpen && event_1.pointerType !== "touch" && event_1.button === 0 && !event_1.ctrlKey) {
        event_1.preventDefault();
      }
    };
    $[13] = settingsMenuOpen;
    $[14] = t11;
  } else {
    t11 = $[14];
  }
  let t12;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <SettingsIcon className="size-[18px]" />;
    $[15] = t12;
  } else {
    t12 = $[15];
  }
  let t13;
  if ($[16] !== expanded || $[17] !== hideLabelWhenCollapsed) {
    t13 = <RailRowContents icon={t12} label="Settings" expanded={expanded} hideLabelWhenCollapsed={hideLabelWhenCollapsed} />;
    $[16] = expanded;
    $[17] = hideLabelWhenCollapsed;
    $[18] = t13;
  } else {
    t13 = $[18];
  }
  let t14;
  if ($[19] !== settingsPrefetchIntent.onFocus || $[20] !== settingsPrefetchIntent.onMouseEnter || $[21] !== t11 || $[22] !== t13) {
    t14 = <DropdownMenuTrigger asChild={true}><button ref={settingsTriggerRef} type="button" aria-label="Settings menu" className={RAIL_ROW} onPointerEnter={openSettingsMenu} onPointerLeave={scheduleSettingsMenuClose} onMouseEnter={settingsPrefetchIntent.onMouseEnter} onFocus={settingsPrefetchIntent.onFocus} onPointerDown={t11}>{t13}</button></DropdownMenuTrigger>;
    $[19] = settingsPrefetchIntent.onFocus;
    $[20] = settingsPrefetchIntent.onMouseEnter;
    $[21] = t11;
    $[22] = t13;
    $[23] = t14;
  } else {
    t14 = $[23];
  }
  let t15;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = event_3 => {
      const target = event_3.target;
      if (target instanceof Node && settingsTriggerRef.current?.contains(target)) {
        event_3.preventDefault();
      }
    };
    $[24] = t15;
  } else {
    t15 = $[24];
  }
  let t16;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <SettingsIcon />;
    $[25] = t16;
  } else {
    t16 = $[25];
  }
  const t17 = !!currentOrg;
  let t18;
  if ($[26] !== settingsHref || $[27] !== t17) {
    t18 = <DropdownMenuItem asChild={true} icon={t16}><RoutePrefetchLink href={settingsHref} prefetchOnIntent={t17}>Settings</RoutePrefetchLink></DropdownMenuItem>;
    $[26] = settingsHref;
    $[27] = t17;
    $[28] = t18;
  } else {
    t18 = $[28];
  }
  let t19;
  if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = <LifeBuoy />;
    $[29] = t19;
  } else {
    t19 = $[29];
  }
  const t20 = !!currentOrg;
  let t21;
  if ($[30] !== supportHref || $[31] !== t20) {
    t21 = <DropdownMenuItem asChild={true} icon={t19}><RoutePrefetchLink href={supportHref} prefetchOnIntent={t20}>Contact Support</RoutePrefetchLink></DropdownMenuItem>;
    $[30] = supportHref;
    $[31] = t20;
    $[32] = t21;
  } else {
    t21 = $[32];
  }
  let t22;
  if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = <DropdownMenuItem asChild={true} icon={<BookOpen />}><a href="https://docs.blacksmith.sh" target="_blank" rel="noopener noreferrer">Documentation...</a></DropdownMenuItem>;
    $[33] = t22;
  } else {
    t22 = $[33];
  }
  let t23;
  if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = <Palette />;
    $[34] = t23;
  } else {
    t23 = $[34];
  }
  let t24;
  let t25;
  if ($[35] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = <DropdownMenuControlRow icon={t23} label="Appearance"><AppearanceThemeToggle /></DropdownMenuControlRow>;
    t25 = <DropdownMenuSeparator />;
    $[35] = t24;
    $[36] = t25;
  } else {
    t24 = $[35];
    t25 = $[36];
  }
  let t26;
  if ($[37] === Symbol.for("react.memo_cache_sentinel")) {
    t26 = <LogOut />;
    $[37] = t26;
  } else {
    t26 = $[37];
  }
  let t27;
  if ($[38] !== login) {
    t27 = login ? <span className="block max-w-[200px] truncate" title={login}>{login}</span> : undefined;
    $[38] = login;
    $[39] = t27;
  } else {
    t27 = $[39];
  }
  let t28;
  if ($[40] !== handleLogout || $[41] !== t27) {
    t28 = <DropdownMenuItem icon={t26} trailing={t27} onClick={handleLogout}>Log out</DropdownMenuItem>;
    $[40] = handleLogout;
    $[41] = t27;
    $[42] = t28;
  } else {
    t28 = $[42];
  }
  let t29;
  if ($[43] !== t18 || $[44] !== t21 || $[45] !== t28) {
    t29 = <DropdownMenuContent side="right" align="start" sideOffset={8} alignOffset={-31} className="min-w-[216px] max-w-[320px]" onPointerEnter={openSettingsMenu} onPointerLeave={scheduleSettingsMenuClose} onCloseAutoFocus={_temp2} onPointerDownOutside={t15}>{t18}{t21}{t22}{t24}{t25}{t28}</DropdownMenuContent>;
    $[43] = t18;
    $[44] = t21;
    $[45] = t28;
    $[46] = t29;
  } else {
    t29 = $[46];
  }
  let t30;
  if ($[47] !== settingsMenuOpen || $[48] !== t14 || $[49] !== t29) {
    t30 = <DropdownMenu modal={false} open={settingsMenuOpen} onOpenChange={setSettingsMenuOpen}>{t14}{t29}</DropdownMenu>;
    $[47] = settingsMenuOpen;
    $[48] = t14;
    $[49] = t29;
    $[50] = t30;
  } else {
    t30 = $[50];
  }
  const settingsMenu = t30;
  const t31 = hideLabelWhenCollapsed && !expanded;
  const t32 = currentOrg ? true : null;
  const t33 = !!currentOrg;
  let t34;
  if ($[51] !== onSectionClick) {
    t34 = onSectionClick?.("usage");
    $[51] = onSectionClick;
    $[52] = t34;
  } else {
    t34 = $[52];
  }
  const t35 = activeSection === "usage" ? "page" : undefined;
  const t36 = activeSection === "usage" && RAIL_ROW_ACTIVE;
  let t37;
  if ($[53] !== t36) {
    t37 = cn(RAIL_ROW, t36);
    $[53] = t36;
    $[54] = t37;
  } else {
    t37 = $[54];
  }
  let t38;
  if ($[55] === Symbol.for("react.memo_cache_sentinel")) {
    t38 = <BarChart3 className="size-[18px]" />;
    $[55] = t38;
  } else {
    t38 = $[55];
  }
  let t39;
  if ($[56] !== expanded || $[57] !== hideLabelWhenCollapsed) {
    t39 = <RailRowContents icon={t38} label="Usage and Billing" expanded={expanded} hideLabelWhenCollapsed={hideLabelWhenCollapsed} />;
    $[56] = expanded;
    $[57] = hideLabelWhenCollapsed;
    $[58] = t39;
  } else {
    t39 = $[58];
  }
  let t40;
  if ($[59] !== t32 || $[60] !== t33 || $[61] !== t34 || $[62] !== t35 || $[63] !== t37 || $[64] !== t39 || $[65] !== usageHref) {
    t40 = <RoutePrefetchLink href={usageHref} prefetch={t32} prefetchOnIntent={t33} onClick={t34} aria-label="Usage and Billing" aria-current={t35} className={t37}>{t39}</RoutePrefetchLink>;
    $[59] = t32;
    $[60] = t33;
    $[61] = t34;
    $[62] = t35;
    $[63] = t37;
    $[64] = t39;
    $[65] = usageHref;
    $[66] = t40;
  } else {
    t40 = $[66];
  }
  let t41;
  if ($[67] !== t31 || $[68] !== t40) {
    t41 = <CollapsedIconTooltip label="Usage and Billing" enabled={t31}>{t40}</CollapsedIconTooltip>;
    $[67] = t31;
    $[68] = t40;
    $[69] = t41;
  } else {
    t41 = $[69];
  }
  const usage = t41;
  let t42;
  if ($[70] !== className) {
    t42 = cn("flex flex-col gap-1", className);
    $[70] = className;
    $[71] = t42;
  } else {
    t42 = $[71];
  }
  let t43;
  if ($[72] !== notifications || $[73] !== settingsMenu || $[74] !== status || $[75] !== t42 || $[76] !== usage) {
    t43 = <div className={t42}>{usage}{settingsMenu}{notifications}{status}</div>;
    $[72] = notifications;
    $[73] = settingsMenu;
    $[74] = status;
    $[75] = t42;
    $[76] = usage;
    $[77] = t43;
  } else {
    t43 = $[77];
  }
  return t43;
}
function _temp2(event_2) {
  return event_2.preventDefault();
}